import React, { useCallback, useState } from 'react';
import { TooltipIdStr, Popover, PopoverBody, OverlayTrigger, Tooltip } from 'ui';
import MessageDate from '../MessageDate';
import useUser from 'services/hooks/useUser';
import CONSTS from 'constants/index.js';

export const MessageMetadataContainer = ({ children, className }) => {
  return (
    <div className={`${className || 'conversation-message-meta'}`}>
      {children}
    </div>
  );
};

export const DeletedMessageMetadata = ({ message }) => {
  const { user: deleter } = useUser(message.metadata.deletedBy);
  return (
    <MessageMetadataContainer>
      <span>Deleted by</span>{' '}
      {deleter.full_name || `${deleter.first_name} ${deleter.last_name}`}
      <MessageDate received_at={message.metadata.deletedAt} />
    </MessageMetadataContainer>
  );
};

export const EmailMessageMetadata = ({ message }) => {
  const firstTo = message.metadata?.to[0];
  if (!firstTo) {
    return null;
  }
  const tos = message.metadata.to;
  const ccs = message.metadata?.cc || [];
  const bccs = message.metadata?.bcc || [];

  const totalCount = tos.length + ccs.length + bccs.length;

  const toMessage = totalCount > 1 ? `${totalCount} people` : '1 person';

  const toDisplay = () => (to) => {
    return (
      <div className="conversation-message-meta-to-popover" key={to} title={to}>
        <a href={`mailto:${to}`} key={to} title={to}>
          {to}
        </a>
      </div>
    );
  };

  return (
    <>
      <div className="popover-list">
        <span className="source">Sent to:</span>
        <span className="detail">{toMessage}</span>
      </div>
      <div className="popover-list">
        <span className="source">To:</span>
        <span className="detail"> {tos.map(toDisplay('To'))}</span>
      </div>
      {ccs.length > 0 && (
        <div className="popover-list">
          <span className="source">Cc:</span>
          <span className="detail">{ccs.map(toDisplay('CC'))}</span>
        </div>
      )}
      {bccs.length > 0 && (
        <div className="popover-list">
          <span className="source">Bcc:</span>
          <span className="detail">{bccs.map(toDisplay('BCC'))}</span>
        </div>
      )}
    </>
  );
};

export const BotResponseMetadata = ({ message }) => {
  return (
    <MessageMetadataContainer>
      <div className="popover-bot-answer">
        <span className="source">Bot Answer Invoked: </span>
        <span className="detail">
          <a
            target="_blank"
            href={`#/bots/${message.metadata.bot_id}/answers/${message.metadata.bot_response_id}`}
            title={message.metadata.bot_response_name}
          >
            {message.metadata.bot_response_name}
          </a>
        </span>
      </div>
    </MessageMetadataContainer>
  );
};

export const SystemInformationMessageMetadata = ({ message }) => {
  return (
    <>
      <MessageMetadataContainer>
        <span id={'recieved_at' + TooltipIdStr(message.received_at)}>
          <MessageDate received_at={message.received_at} />
        </span>
      </MessageMetadataContainer>
    </>
  );
};

const MessageInfoTooltip = ({
  message,
  displayedChannel,
  toPopoverOpen,
  setToPopoverOpen,
  messageIsEmail,
  messageIsBotResponse,
}) => {
  if (!message) {
    return null;
  }

  const renameSource = (source = '') => {
    if (source === 'watson') {
      return 'IBM Watson';
    }
    if (source === 'chatgpt') {
      return 'ChatGPT';
    }
    return source;
  };

  return (
    <>
      <i
        className={`far fa-info-circle message-info-tooltip-icon`}
        id={`message_source_` + TooltipIdStr(message.received_at)}
      ></i>
      <Popover
        placement="bottom"
        isOpen={toPopoverOpen}
        target={`message_source_` + TooltipIdStr(message.received_at)}
        toggle={() => setToPopoverOpen(!toPopoverOpen)}
        trigger="legacy"
      >
        <PopoverBody>
          {message.source && (
            <>
              <div className="popover-list">
                <span className="source">Source:</span>
                <span>{renameSource(message.source)}</span>
              </div>
            </>
          )}
          {message.metadata && message.metadata.url && (
            <>
              <div className="popover-list">
                <span className="source">Page:</span>
                <span>
                  {message.metadata.url === 'about:blank' ? (
                    <a href="/#/bots">Practice Bot</a>
                  ) : (
                    <a href={message.metadata.url} target="_blank">
                      {message.metadata.hostpage_title || message.metadata.url}
                    </a>
                  )}
                </span>
              </div>
            </>
          )}
          {displayedChannel && (
            <>
              <div className="popover-list">
                <span className="source">Channel:</span>
                <span>{displayedChannel?.name || 'Unknown'}</span>
              </div>
            </>
          )}
          {messageIsEmail ? <EmailMessageMetadata message={message} /> : null}
          {messageIsBotResponse ? (
            <BotResponseMetadata message={message} />
          ) : null}
        </PopoverBody>
      </Popover>
    </>
  );
};

export const MessageMetadata = ({
  message,
  displayedChannel,
  messageIsEmail,
  messageIsBotResponse,
}) => {
  const [toPopoverOpen, setToPopoverOpen] = useState(false);

  const statusIconStyle =
    CONSTS.MESSAGE_STATUS_ICON_CLASSES[message.status] ||
    'fas fa-question-circle default-grey';
  const failedMessage = message.status === CONSTS.MESSAGE_STATUS_FAILED;
  const getErrorMessage = useCallback(() => {
    if (failedMessage) {
      if (message.error_message) {
        return message.error_message;
      }
      return CONSTS.MESSAGE_STATUS_DESCRIPTIONS[message.status];
    }
    return;
  }, [failedMessage, message.error_message, message.status]);
  const toolTipText =
    getErrorMessage() ||
    CONSTS.MESSAGE_STATUS_DESCRIPTIONS[message.status] ||
    `Unknown message status: ${message.status}`;
  return (
    <MessageMetadataContainer>
      <MessageDate received_at={message.received_at} />
      {' - '}
      {'   '}
      <OverlayTrigger
        placement="top"
        delay={{ show: 0, hide: 0 }}
        overlay={
          <Tooltip target={'message_tooltip_' + message.id}>
            {toolTipText}
          </Tooltip>
        }
      >
        <i
          id={'message_tooltip_' + message.id}
          className={`${statusIconStyle}`}
        ></i>
      </OverlayTrigger>
      {' - '}
      <MessageInfoTooltip
        message={message}
        displayedChannel={displayedChannel}
        toPopoverOpen={toPopoverOpen}
        setToPopoverOpen={setToPopoverOpen}
        messageIsEmail={messageIsEmail}
        messageIsBotResponse={messageIsBotResponse}
      />
    </MessageMetadataContainer>
  );
};
